import { useEffect, useState } from 'react';
import { FaKey, FaLock, FaSpinner } from 'react-icons/fa';
import { FiArrowLeft, FiEye, FiEyeOff, FiMail } from 'react-icons/fi';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Field } from '../components/a11y';
import { OnePerLink } from '../components/auth/OnePerLink';
import FormError from '../components/FormError';
import { useAuthStore } from '../stores/auth.store';
import { fireAndForget } from '../utils/fireAndForget';

/**
 * Reset Password Component
 * Allows users to reset their password using a token
 */
function ResetPasswordForm() {
  // Navigation hook and URL params
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const emailParam = searchParams.get('email');

  // Form state management
  const [email] = useState(emailParam || '');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Auth store actions and state
  const resetPassword = useAuthStore((state) => state.resetPassword);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthLoading = useAuthStore((state) => state.isLoading);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isAuthLoading) {
      void navigate('/', { replace: true });
    }
  }, [isAuthenticated, isAuthLoading, navigate]);

  // Redirect if no token or email
  useEffect(() => {
    if (!token || !emailParam) {
      toast.error('Invalid or missing reset token. Please request a new password reset.');
      void navigate('/forgot-password', { replace: true });
    }
  }, [token, emailParam, navigate]);

  /**
   * Handle form submission
   * Resets the user's password
   */
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (password !== passwordConfirmation) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const { error, message } = await resetPassword(
        email,
        token || '',
        password,
        passwordConfirmation,
      );
      if (error) {
        toast.error(error);
      } else {
        toast.success(
          message || 'Password reset successful! You can now login with your new password.',
        );
        // Redirect to login after successful reset
        setTimeout(() => {
          void navigate('/login', { replace: true });
        }, 2000);
      }
    } catch {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-base-100 to-secondary/5 safe-area-horizontal">
      <div className="flex flex-col justify-center items-center min-h-screen p-4 safe-area-vertical">
        <div className="w-full max-w-sm">
          {/* Header Section */}
          <div className="text-center mb-8">
            <div className="mb-6">
              <img
                src="/logo.webp"
                alt="Logo"
                className="h-16 w-16 mx-auto rounded-2xl shadow-lg"
              />
            </div>
            <h1 className="text-3xl font-bold text-base-content mb-2">Reset Password</h1>
            <p className="text-base-content/60 text-sm">Enter your new password below</p>
          </div>

          {/* Form Card */}
          <div className="bg-base-100/90 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-base-200">
            <form className="space-y-5" onSubmit={fireAndForget(handleSubmit)}>
              {/* Email Field (Read Only) */}
              <div className="space-y-2">
                <Field
                  label={<span className="text-sm font-medium text-base-content">Email</span>}
                  variant="plain"
                  labelClassName="block"
                  controlId="email"
                >
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      value={email}
                      className="w-full pl-11 pr-4 py-3 border border-base-300 rounded-lg outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-base-200"
                      placeholder="Email address"
                      required
                      readOnly
                    />
                    <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50" />
                  </div>
                </Field>
              </div>

              {/* New Password Field */}
              <div className="space-y-2">
                <Field
                  label={
                    <span className="text-sm font-medium text-base-content">New Password</span>
                  }
                  variant="plain"
                  labelClassName="block"
                  controlId="password"
                >
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setPassword(e.target.value)
                      }
                      className="w-full pl-11 pr-11 py-3 border border-base-300 rounded-lg outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                      placeholder="Enter new password"
                      required
                      minLength={8}
                    />
                    <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50" />
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-(--ee-tw-gray-500) hover:text-primary transition-colors z-10 cursor-pointer"
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </Field>
                <p className="text-xs text-base-content/50">Must be at least 8 characters long</p>
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <Field
                  label={
                    <span className="text-sm font-medium text-base-content">
                      Confirm New Password
                    </span>
                  }
                  variant="plain"
                  labelClassName="block"
                  controlId="passwordConfirmation"
                >
                  <div className="relative">
                    <input
                      type={showPasswordConfirmation ? 'text' : 'password'}
                      id="passwordConfirmation"
                      value={passwordConfirmation}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setPasswordConfirmation(e.target.value)
                      }
                      className="w-full pl-11 pr-11 py-3 border border-base-300 rounded-lg outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                      placeholder="Confirm new password"
                      required
                      minLength={8}
                    />
                    <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50" />
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-(--ee-tw-gray-500) hover:text-primary transition-colors z-10 cursor-pointer"
                    >
                      {showPasswordConfirmation ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </Field>
                {password && passwordConfirmation && password !== passwordConfirmation ? (
                  <FormError errors={'Passwords do not match'} />
                ) : null}
              </div>

              {/* Submit Button */}
              <button
                className="w-full h-12 bg-primary hover:bg-primary-focus disabled:bg-base-300 disabled:cursor-not-allowed text-primary-content font-medium rounded-lg transition-colors flex items-center justify-center"
                disabled={
                  isLoading ||
                  !password ||
                  !passwordConfirmation ||
                  password !== passwordConfirmation
                }
                type="submit"
              >
                {isLoading ? (
                  <>
                    <FaSpinner className="animate-spin -ml-1 mr-3 h-5 w-5 text-(--ee-on-accent)" />
                    Resetting Password...
                  </>
                ) : (
                  <>
                    <FaKey className="mr-2" />
                    Reset Password
                  </>
                )}
              </button>
            </form>

            {/* Back to Login Link */}
            <div className="text-center mt-6">
              <Link
                className="text-sm font-medium text-primary hover:text-primary-focus transition-colors inline-flex items-center gap-2"
                to="/login"
              >
                <FiArrowLeft className="text-xs" />
                Back to Login
              </Link>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-xs text-base-content/50">
              Remember your password?{' '}
              <Link to="/login" className="text-primary hover:text-primary-focus">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Each link gets its own form: the address is copied from the link once, so a
 * second reset link must not keep the first one's (c-083).
 */
function ResetPassword() {
  return (
    <OnePerLink>
      <ResetPasswordForm />
    </OnePerLink>
  );
}

export default ResetPassword;
